import {
  ApiError,
  ERROR_CODES,
  type ApiErrorDetails,
  type ApiLanguage,
  type ErrorCode,
} from '@luacentrix/shared';
import type { ErrorResponseBody } from '@luacentrix/contracts';
import { newIdempotencyKey, newRequestId } from './transport';

export const KNOWN_ERROR_CODES: readonly string[] = Object.values(ERROR_CODES);

export interface ParsedEnvelope {
  code: ErrorCode;
  message: string;
  details: ApiErrorDetails | undefined;
  requestId: string | undefined;
}

export function isErrorEnvelopeBody(value: unknown): value is ErrorResponseBody {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const envelope = value as { error?: unknown };
  if (envelope.error === null || typeof envelope.error !== 'object') {
    return false;
  }
  const body = envelope.error as { code?: unknown; message?: unknown };
  return typeof body.code === 'string' && typeof body.message === 'string';
}

export function parseErrorEnvelope(value: unknown): ParsedEnvelope {
  if (!isErrorEnvelopeBody(value)) {
    throw new ApiError(ERROR_CODES.UNSUPPORTED_SERVER_STATE, {
      details: { field: 'error_envelope', reason: 'malformed' },
    });
  }
  const body = value.error;
  const known = (KNOWN_ERROR_CODES as readonly string[]).includes(body.code);
  return {
    code: (known ? body.code : ERROR_CODES.INTERNAL_ERROR) as ErrorCode,
    message: body.message,
    details: body.details,
    requestId: body.request_id,
  };
}

export function apiErrorFromResponse(
  response: Response,
  envelope: ParsedEnvelope,
  requestId: string,
): ApiError {
  const details: ApiErrorDetails = {
    ...(envelope.details ?? {}),
    http_status: response.status,
    request_id: envelope.requestId ?? requestId,
  };
  if (!response.ok && envelope.code === ERROR_CODES.INTERNAL_ERROR && response.status < 500) {
    details.transport_status = response.status;
  }
  return new ApiError(envelope.code, {
    message: envelope.message,
    details,
    status: response.status,
  });
}

export function apiErrorFromTransport(
  reason: unknown,
  timedOut: boolean,
  requestId: string,
): ApiError {
  if (timedOut) {
    return new ApiError(ERROR_CODES.REQUEST_TIMEOUT, {
      details: { request_id: requestId, reason: 'client_deadline' },
    });
  }
  if (reason instanceof Error && reason.name === 'AbortError') {
    return new ApiError(ERROR_CODES.REQUEST_TIMEOUT, {
      details: { request_id: requestId, reason: 'aborted_by_caller' },
    });
  }
  return new ApiError(ERROR_CODES.SERVICE_UNAVAILABLE, {
    details: {
      request_id: requestId,
      reason: reason instanceof Error ? reason.name : 'network_failure',
    },
  });
}

export function headerValue(headers: Headers, name: string): string | null {
  return headers.get(name);
}

export function echoRequestId(headers: Headers, fallback: string): string {
  return headerValue(headers, 'x-request-id') ?? fallback;
}

export function requestIdentifierFor(scope: string): { requestId: string; idempotencyKey: string } {
  return { requestId: newRequestId(), idempotencyKey: newIdempotencyKey(scope) };
}

export function languageFromAccept(value: string | null | undefined): ApiLanguage | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  return value.toLowerCase().includes('th') ? 'th' : 'en';
}
